import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Heading,
  Input,
  Stack,
  Text,
  useColorModeValue,
} from '@chakra-ui/react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FiKey } from 'react-icons/fi'
import { z } from 'zod'
import { useAuth } from '@/contexts/AuthContext'
import { useChangeAdminPassword } from '@/hooks/useAdminData'
import { useToast } from '@/hooks/useToast'
import { resolveApiErrorMessage } from '@/services/admin/baseService'
import { TEXT_CONSTRAINTS } from '@shared/inputConstraints'
import { validatePasswordStrength } from '@shared/passwordStrength'
import { validateTextWithConstraint } from '@shared/textConstraintHelpers'

const passwordConstraint = TEXT_CONSTRAINTS['user.password']

const accountPasswordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Current password is required'),
    newPassword: z
      .string()
      .min(
        passwordConstraint.minLength ?? 8,
        `Password must be at least ${passwordConstraint.minLength ?? 8} characters`
      )
      .max(
        passwordConstraint.maxLength ?? 128,
        `Password must be at most ${passwordConstraint.maxLength ?? 128} characters`
      )
      .superRefine((password, context) => {
        const strength = validatePasswordStrength(password)
        if (!strength.valid) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            message: strength.errors[0] ?? 'Password does not meet complexity requirements',
          })
        }
      }),
    confirmPassword: z.string(),
  })
  .refine(data => data.newPassword === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  })

type AccountPasswordFormData = z.infer<typeof accountPasswordSchema>

export function AdminPasswordPanel() {
  const { t } = useTranslation()
  const { logout, user } = useAuth()
  const toast = useToast()
  const changePasswordMutation = useChangeAdminPassword()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const mutedColor = useColorModeValue('gray.600', 'gray.300')
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<AccountPasswordFormData>({
    resolver: zodResolver(accountPasswordSchema),
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  })

  const onSubmit = async (data: AccountPasswordFormData) => {
    if (!user?.id) {
      return
    }

    setIsSubmitting(true)
    try {
      const currentPassword = validateTextWithConstraint(
        data.currentPassword,
        passwordConstraint,
        { requiredOverride: true }
      )
      const newPassword = validateTextWithConstraint(
        data.newPassword,
        passwordConstraint,
        { requiredOverride: true }
      )

      if (!currentPassword.valid || !currentPassword.value) {
        toast.error({
          titleKey: 'account.password.error',
          descriptionKey: 'account.password.currentPasswordInvalid',
        })
        return
      }
      if (!newPassword.valid || !newPassword.value) {
        toast.error({
          titleKey: 'account.password.error',
          descriptionKey: 'account.password.validationFailed',
        })
        return
      }

      await changePasswordMutation.mutateAsync({
        userId: user.id,
        data: {
          current_password: currentPassword.value,
          new_password: newPassword.value,
        },
      })
      reset()
      toast.success({
        titleKey: 'account.password.success',
        descriptionKey: 'account.password.successDescription',
      })
      logout('admin-password-changed')
    } catch (error: unknown) {
      toast.error({
        titleKey: 'account.password.error',
        description: resolveApiErrorMessage(error, t('common.unknownError')),
        duration: 6000,
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Stack spacing="6" maxW="640px">
      <Stack spacing="1">
        <Heading size="sm">{t('account.password.title')}</Heading>
        <Text color={mutedColor} fontSize="sm">
          {t('account.password.description')}
        </Text>
      </Stack>

      <Alert status="info">
        <AlertIcon />
        <AlertDescription>{t('account.password.sessionNotice')}</AlertDescription>
      </Alert>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing="4">
          <FormControl isInvalid={Boolean(errors.currentPassword)} isRequired>
            <FormLabel>{t('account.password.currentPassword')}</FormLabel>
            <Input
              type="password"
              autoComplete="current-password"
              maxLength={passwordConstraint.maxLength}
              {...register('currentPassword')}
            />
            <FormErrorMessage>{errors.currentPassword?.message}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={Boolean(errors.newPassword)} isRequired>
            <FormLabel>{t('account.password.newPassword')}</FormLabel>
            <Input
              type="password"
              autoComplete="new-password"
              maxLength={passwordConstraint.maxLength}
              {...register('newPassword')}
            />
            <FormHelperText>{t('account.password.passwordHelp')}</FormHelperText>
            <FormErrorMessage>{errors.newPassword?.message}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={Boolean(errors.confirmPassword)} isRequired>
            <FormLabel>{t('account.password.confirmPassword')}</FormLabel>
            <Input
              type="password"
              autoComplete="new-password"
              maxLength={passwordConstraint.maxLength}
              {...register('confirmPassword')}
            />
            <FormErrorMessage>{errors.confirmPassword?.message}</FormErrorMessage>
          </FormControl>

          <Button
            type="submit"
            alignSelf="flex-start"
            colorScheme="admin"
            leftIcon={<FiKey />}
            isLoading={isSubmitting}
          >
            {t('account.password.action')}
          </Button>
        </Stack>
      </form>
    </Stack>
  )
}
